/**
 * @file KeyDates.jsx
 * @description AI EXPO JORDAN 2026 — "Key Dates" section.
 *   Redesigned to match the Command Bridge visual language used across the
 *   site (About, WhosThisFor). Brand discipline:
 *     · Base palette: dark gray #151515 / black, white text
 *     · Accents (purple #7a3cff, azure #2cabe2) reserved for hover and
 *       animation only — never resting fills
 *     · Oswald display + JetBrains Mono for HUD/technical labels
 *
 *   The timeline reads as a mission countdown: each milestone is a "mission
 *   step" with its own console-style block. The final entry — Expo Day —
 *   is the destination and uses purple as the rare accent.
 *
 *   Content is preserved verbatim from the previous edition.
 *
 * @author AI EXPO Web Team
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../ComponentsStyling/keyDates.css';

/* Utility functions */

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------- */
/*  Section content                                                           */
/* -------------------------------------------------------------------------- */

const ENTRIES = [
    {
        index: '01',
        status: 'OPEN',
        dateTop: '10 — 20',
        dateBottom: 'JULY 2026',
        title: 'Submission Window',
        body: 'Submit your AI project through the official website. The submission window runs 10 – 20 July 2026.',
        accent: 'azure',
    },
    {
        index: '02',
        status: 'FREE',
        dateTop: '09 · 16 · 23',
        dateBottom: 'MAY 2026',
        title: 'Three Pre-Event Workshops',
        body: 'Three free hands-on sessions for all applicants — building, deploying, and pitching a stronger AI project.',
        accent: 'azure',
    },
    {
        index: '03',
        status: 'REVIEW',
        dateTop: 'TBA',
        dateBottom: '2026',
        title: 'Proposal Review',
        body: 'A review panel evaluates every submission. Selected teams are notified and invited to participate in Expo Day.',
        accent: 'azure',
    },
    {
        index: '04',
        status: 'MAIN EVENT',
        dateTop: '01 & 03',
        dateBottom: 'OCTOBER 2026',
        title: 'Expo Day (Two Days)',
        body: 'Two days of live AI project demos, company booths, stage talks, networking, judging, and the closing awards ceremony — at UJ Academy. Day 1 (1 October) — Evaluation & Industry Day; Day 2 (3 October) — Finals, Awards & Exhibition.',
        accent: 'destination',
    },
];

/* -------------------------------------------------------------------------- */
/*  Mouse-follow spotlight                                                    */
/* -------------------------------------------------------------------------- */

const useSpotlight = (ref) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;

        let raf = 0;
        let pending = null;

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            pending = {
                x: ((e.clientX - rect.left) / rect.width) * 100,
                y: ((e.clientY - rect.top) / rect.height) * 100,
            };
            if (!raf) {
                raf = requestAnimationFrame(() => {
                    if (pending) {
                        el.style.setProperty('--mx', pending.x + '%');
                        el.style.setProperty('--my', pending.y + '%');
                    }
                    raf = 0;
                });
            }
        };

        el.addEventListener('mousemove', onMove);
        return () => {
            el.removeEventListener('mousemove', onMove);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref]);
};

/* -------------------------------------------------------------------------- */
/*  Rail progress hook — tracks scroll within the timeline                    */
/* -------------------------------------------------------------------------- */

/* Drives the timeline rail fill straight from scroll — writes `--p` to the fill
   element via ref (no React state, no re-render on scroll) and throttles to one
   update per animation frame, so scrolling stays smooth on every device. */
const useRailProgress = (ref) => {
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const fill = el.querySelector('.kd__rail-fill');
        if (!fill) return;

        if (prefersReducedMotion()) {
            fill.style.setProperty('--p', '1');
            return;
        }

        let raf = 0;
        const update = () => {
            raf = 0;
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight;
            // Fill from when the timeline top hits 70% of viewport and complete
            // when its bottom hits 30% of viewport.
            const start = vh * 0.7;
            const end = -rect.height + vh * 0.3;
            const total = start - end;
            const p = total > 0 ? Math.max(0, Math.min(1, (start - rect.top) / total)) : 0;
            fill.style.setProperty('--p', String(p));
        };
        const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref]);
};

/* -------------------------------------------------------------------------- */
/*  Section                                                                   */
/* -------------------------------------------------------------------------- */

const KeyDates = () => {
    const ref = useRef(null);
    const timelineRef = useRef(null);
    const [visible, setVisible] = useState(false);

    useSpotlight(ref);
    useRailProgress(timelineRef);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    setVisible(true);
                    obs.unobserve(el);
                }
            });
        }, { threshold: 0.12 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className={`kd${visible ? ' is-visible' : ''}`}
            id="key-dates"
        >
            {/* Background layers */}
            <div className="kd__grid" aria-hidden />
            <div className="kd__spot" aria-hidden />
            <div className="kd__noise" aria-hidden />

            <div className="kd__wrap">

                {/* ───────── HUD STRIP ───────── */}
                <div className="kd__hud" aria-hidden>
                    <span className="kd__hud-tag">
                        <span className="kd__hud-dot" />
                        SEC&nbsp;//&nbsp;04
                    </span>
                    <span className="kd__hud-line" />
                    <span className="kd__hud-mid">KEY&nbsp;DATES&nbsp;·&nbsp;MISSION&nbsp;TIMELINE</span>
                    <span className="kd__hud-line" />
                    <span className="kd__hud-tag kd__hud-tag--right">
                        EDITION&nbsp;01
                        <span className="kd__hud-blink" />
                    </span>
                </div>

                {/* ───────── HEADER ───────── */}
                <header className="kd__head">
                    <div className="kd__eyebrow">
                        <span className="kd__eyebrow-bracket">[</span>
                        <span>Mark your calendar</span>
                        <span className="kd__eyebrow-bracket">]</span>
                    </div>

                    <h2 className="kd__title">
                        <span className="kd__title-row">
                            <span className="kd__title-num" aria-hidden>03</span>
                            <span className="kd__title-text">Four moments</span>
                        </span>
                        <span className="kd__title-row kd__title-row--alt">
                            <span className="kd__title-text">before</span>
                            <span className="kd__title-text kd__title-text--outline">Expo&nbsp;Day.</span>
                        </span>
                    </h2>

                    <div className="kd__lede-wrap">
                        <span className="kd__lede-bar" aria-hidden />
                        <p className="kd__lede">
                            Here are the dates that matter most — bookmark them and plan ahead.
                        </p>
                    </div>
                </header>

                {/* ───────── TIMELINE ───────── */}
                <div className="kd__timeline" ref={timelineRef}>

                    {/* Continuous vertical rail with scroll-fill */}
                    <div className="kd__rail" aria-hidden>
                        <span className="kd__rail-track" />
                        <span className="kd__rail-fill" />
                        {/* Tick marks */}
                        <span className="kd__rail-tick kd__rail-tick--top" />
                        <span className="kd__rail-tick kd__rail-tick--bot" />
                    </div>

                    {ENTRIES.map((e, i) => (
                        <article
                            key={e.index}
                            className={`kd-entry kd-entry--${e.accent}${i === ENTRIES.length - 1 ? ' kd-entry--last' : ''}`}
                            style={{ '--i': i }}
                        >
                            {/* Rail node */}
                            <span className="kd-entry__node" aria-hidden>
                                <span className="kd-entry__node-core" />
                                <span className="kd-entry__node-ring" />
                                <span className="kd-entry__node-pulse" />
                            </span>

                            {/* Connector line from rail to card */}
                            <span className="kd-entry__connector" aria-hidden />

                            {/* Card */}
                            <div className="kd-entry__card">
                                {/* Corner brackets */}
                                <span className="kd-entry__corner kd-entry__corner--tl" />
                                <span className="kd-entry__corner kd-entry__corner--tr" />
                                <span className="kd-entry__corner kd-entry__corner--bl" />
                                <span className="kd-entry__corner kd-entry__corner--br" />

                                {/* Sweep on hover */}
                                <span className="kd-entry__sweep" aria-hidden />

                                {/* Top strip — index + status */}
                                <div className="kd-entry__strip">
                                    <span className="kd-entry__strip-id">
                                        STEP&nbsp;{e.index}/04
                                    </span>
                                    <span className="kd-entry__status">
                                        <span className="kd-entry__status-led" />
                                        {e.status}
                                    </span>
                                </div>

                                <div className="kd-entry__body">

                                    {/* LEFT — Date block */}
                                    <div className="kd-entry__when">
                                        <span className="kd-entry__date-label">DATE</span>
                                        <div className="kd-entry__date">
                                            <span className="kd-entry__date-top">{e.dateTop}</span>
                                            <span className="kd-entry__date-bottom">{e.dateBottom}</span>
                                        </div>
                                    </div>

                                    {/* Vertical divider */}
                                    <span className="kd-entry__divider" aria-hidden />

                                    {/* RIGHT — Editorial text */}
                                    <div className="kd-entry__text">
                                        <h3 className="kd-entry__title">{e.title}</h3>
                                        <p className="kd-entry__body-text">{e.body}</p>
                                    </div>
                                </div>

                                {/* Bottom progress bar (decorative — fills on hover) */}
                                <span className="kd-entry__progress" aria-hidden />
                            </div>
                        </article>
                    ))}
                </div>

                {/* ───────── CTA ───────── */}
                <footer className="kd__close">
                    <Link to="/agenda" className="kd__cta">
                        <span className="kd__cta-bracket">[</span>
                        <span className="kd__cta-text">See the full schedule</span>
                        <span className="kd__cta-arrow">
                            <svg viewBox="0 0 24 12" fill="none" aria-hidden>
                                <path
                                    d="M1 6h22m0 0L18 1m5 5l-5 5"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="square"
                                />
                            </svg>
                        </span>
                        <span className="kd__cta-bracket">]</span>
                    </Link>
                </footer>

            </div>
        </section>
    );
};

export default KeyDates;